import { inject, Injectable } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { get, isUndefined, values } from 'lodash';
import { forkJoin, lastValueFrom, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { maxAssetLoadingRetries } from '../../../cross-team-shared/misc-services/resources/url-request.service';
import { getFirstVideoNotOrthographicOutput } from '../../../cross-team-shared/misc-utils/output-utils';
import {
  getAngleToPixelUrl,
  getMetadataUrl,
  getPixelToLightrayUrl,
  hasCalibrationData,
} from '../../../cross-team-shared/misc-utils/projection-utils';
import { EventTrackingService } from '../../../shared/event-tracking/event-tracking.service';
import { LutData, LutMetadata } from '../../../team-apis/core/lut-data';
import { Output } from '../../../team-apis/otterdam/outputs/output';
import { Project } from '../../../team-apis/otterdam/project';
import { Task } from '../../../team-apis/otterdam/task';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { getProjectionTypeFromTask } from '../../shared/utils/projection-utils';
import { AbstractResource } from '../abstract-resource';

export const lutPrefixCacheKey = 'lut';

@Injectable()
export class LutLoadingService extends AbstractResource {
  override readonly cachePrefix = lutPrefixCacheKey;

  private store = inject(TaskWorkspaceStore);
  private eventTrackingService = inject(EventTrackingService);

  private loadStart = 0;

  async load() {
    this.loadStart = performance.now();
    const { project } = this.store.state();

    // Assumption: only one video output (non-orthographic) per task is
    // supported when using LUTs
    const videoOutput = getFirstVideoNotOrthographicOutput(project);
    if (!shouldLoadLuts(videoOutput, this.store.state())) {
      return;
    }

    assert(
      isDefined(videoOutput),
      'video output must be defined when loading LUTs',
    );

    const lutData = await this.requestLutData$(videoOutput.name);
    this.store('lutData').set(lutData);
    if (isDefined(lutData)) {
      this.trackEvents(videoOutput.name);
    }
  }

  private requestLutData$(videoOutputName: string) {
    const { pixelToLightrayUrl, angleToPixelUrl, metadataUrl } =
      this.getLutUrls(videoOutputName);

    return lastValueFrom(
      forkJoin([
        this.requestMetadata$(metadataUrl),
        this.requestLUT$(angleToPixelUrl),
        this.requestLUT$(pixelToLightrayUrl),
      ]).pipe(
        map(([metadata, angleToPixel, pixelToLightray]) =>
          mapToLutData(metadata, angleToPixel, pixelToLightray),
        ),
      ),
    );
  }

  private trackEvents(videoOutputName: string) {
    const urls = values(this.getLutUrls(videoOutputName));
    const unsignedUrls = urls.filter(isDefined).map(unsignUrl);

    this.eventTrackingService.track(
      {
        id: 'load_lut_asset',
        bucket: 'untracked',
        duration: performance.now() - this.loadStart,
        total_luts: 1,
        lut_urls: { [videoOutputName]: unsignedUrls },
      },
      { isInteractive: false },
    );
  }

  private getLutUrls(videoOutputName: string) {
    const { task } = this.store.state();

    const includeAngleToPixel =
      getProjectionTypeFromTask(this.store.state(), videoOutputName) === 'lut';

    const angleToPixelUrl = includeAngleToPixel
      ? getAngleToPixelSignedUrl(task, videoOutputName)
      : undefined;

    const metadataUrl = getLutMetadataSignedUrl(task, videoOutputName);
    const pixelToLightrayUrl = getPixelToLightraySignedUrl(
      task,
      videoOutputName,
    );

    assert(metadataUrl, 'metadata URL must be defined when loading LUTs');

    return { angleToPixelUrl, metadataUrl, pixelToLightrayUrl };
  }

  private requestMetadata$(url: string) {
    return this.request$<LutMetadata>('GET', url, {
      responseType: 'json',
      customErrorMessage: 'Failed to load LUT metadata.',
      retryCount: maxAssetLoadingRetries,
      cache: true,
    });
  }

  private requestLUT$(url: string | undefined) {
    if (isUndefined(url)) {
      return of(undefined);
    }

    return this.request$<ArrayBuffer>('GET', url, {
      responseType: 'arraybuffer',
      customErrorMessage: 'Failed to load LUT.',
      retryCount: maxAssetLoadingRetries,
      cache: true,
    });
  }
}

function shouldLoadLuts(
  videoOutput: Output | undefined,
  args: { task: Task; project: Project },
) {
  if (isUndefined(videoOutput)) {
    return false;
  }

  const metadataUrl = getLutMetadataSignedUrl(args.task, videoOutput.name);
  return (
    hasCalibrationData(args.task, videoOutput.name) && isDefined(metadataUrl)
  );
}

function getAngleToPixelSignedUrl(task: Task, videoOutputName: string) {
  const unsignedUrl: string | undefined =
    task.data[getAngleToPixelUrl(videoOutputName)];
  return unsignedUrl ? signUrl(unsignedUrl, task) : undefined;
}

function getLutMetadataSignedUrl(task: Task, videoOutputName: string) {
  const unsignedUrl: string | undefined =
    task.data[getMetadataUrl(videoOutputName)];
  return unsignedUrl ? signUrl(unsignedUrl, task) : undefined;
}

function getPixelToLightraySignedUrl(task: Task, videoOutputName: string) {
  const unsignedUrl: string | undefined =
    task.data[getPixelToLightrayUrl(videoOutputName)];
  return unsignedUrl ? signUrl(unsignedUrl, task) : undefined;
}

function signUrl(url: string, task: Task): string {
  // signed_urls for LUT should have only one entry
  const signedUrl = get(task, ['_extras', 'signed_urls', url, 0]);
  if (!signedUrl) {
    throw new Error(`Signed URL not found for ${url}`);
  }
  return signedUrl;
}

function unsignUrl(url: string) {
  return url.split('?')[0];
}

function mapToLutData(
  metadata: LutMetadata,
  angleToPixel?: ArrayBuffer,
  pixelToLightray?: ArrayBuffer,
): LutData {
  return {
    metadata,
    angleToPixel: angleToPixel ? new Float32Array(angleToPixel) : undefined,
    pixelToLightray: pixelToLightray
      ? new Float32Array(pixelToLightray)
      : undefined,
  };
}
